/** Sidebar entries that receive marked-done work carry this attribute: `mine` or `project:<id>`. */
export const COMPLETED_TARGET_ATTR = 'data-completed-target';

const TICK_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

function visible(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function completedTarget(projectId?: string): HTMLElement | null {
  const stream = projectId ? document.querySelector(`[${COMPLETED_TARGET_ATTR}="project:${projectId}"]`) : null;
  if (visible(stream)) return stream;
  const mine = document.querySelector(`[${COMPLETED_TARGET_ATTR}="mine"]`);
  return visible(mine) ? mine : null;
}

function pulse(target: HTMLElement, reduced: boolean) {
  target.animate(
    reduced
      ? [{ opacity: 1 }, { opacity: 0.55 }, { opacity: 1 }]
      : [
          { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(5,150,105,0)' },
          { transform: 'scale(1.05)', boxShadow: '0 0 0 6px rgba(5,150,105,0.25)' },
          { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(5,150,105,0)' },
        ],
    { duration: 520, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  );
}

/**
 * Sends a tick from `source` into the sidebar's Completed entry, then pulses it.
 * Resolves when the tick lands so callers can close a popup afterwards.
 */
export function flyToCompleted(source: Element | null, projectId?: string): Promise<void> {
  const target = completedTarget(projectId);
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (!target || reduced || !source) {
    if (target) pulse(target, true);
    // Long enough to read the button's "Done" before anything closes.
    return new Promise((resolve) => window.setTimeout(resolve, 450));
  }

  const from = source.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const size = 26;
  const margin = 24;
  const startX = from.left + from.width / 2 - size / 2;
  const startY = from.top + from.height / 2 - size / 2;
  // An off-screen section (e.g. at the foot of a long page) gets the tick at the edge nearest it.
  const endX = Math.min(Math.max(to.left + 22 - size / 2, margin), window.innerWidth - size - margin);
  const endY = Math.min(Math.max(to.top + to.height / 2 - size / 2, margin), window.innerHeight - size - margin);
  const offscreen = to.bottom < 0 || to.top > window.innerHeight;
  const dx = endX - startX;
  const dy = endY - startY;

  const tick = document.createElement('div');
  tick.setAttribute('aria-hidden', 'true');
  tick.innerHTML = TICK_SVG;
  Object.assign(tick.style, {
    position: 'fixed',
    left: `${startX}px`,
    top: `${startY}px`,
    width: `${size}px`,
    height: `${size}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '9999px',
    background: 'rgb(5,150,105)',
    color: 'white',
    boxShadow: '0 8px 20px -6px rgba(5,150,105,0.7)',
    zIndex: '2147483647',
    pointerEvents: 'none',
  });
  document.body.appendChild(tick);

  // A short pop in place, then an arc up and over to the sidebar.
  const lift = Math.min(120, Math.abs(dx) * 0.25 + 40);
  const flight = tick.animate(
    [
      { transform: 'translate(0, 0) scale(0.4)', opacity: 0, offset: 0 },
      { transform: 'translate(0, 0) scale(1.15)', opacity: 1, offset: 0.16 },
      { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0.26 },
      { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - lift}px) scale(0.9)`, opacity: 1, offset: 0.62 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.55)`, opacity: offscreen ? 0 : 0.9, offset: 1 },
    ],
    { duration: 820, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'forwards' },
  );

  return new Promise((resolve) => {
    const land = () => {
      tick.remove();
      if (!offscreen) pulse(target, false);
      resolve();
    };
    flight.onfinish = land;
    flight.oncancel = land;
  });
}
